/**
 * useBackgroundActivity Hook
 *
 * Implements multiple strategies to keep the PWA active when minimized/backgrounded:
 *
 * 1. Silent Audio Playback - Plays silent audio to keep browser "active" on Android
 * 2. Web Locks API - Prevents page from being discarded by browser
 * 3. Local Notifications - Reminds user to return to the app when backgrounded
 * 4. Video Element Recovery - Fixes frozen video frames when returning from background
 *
 * IMPORTANT: These are "best effort" hacks. iOS Safari WILL still kill the app.
 * For production baby monitoring, a native app is recommended.
 */

import { useEffect, useRef, useCallback, useState } from "react";

interface BackgroundActivityState {
  isBackgrounded: boolean;
  notificationPermission: NotificationPermission | "unsupported";
  webLockHeld: boolean;
  silentAudioPlaying: boolean;
  /** Request notification permission manually - call this on user interaction */
  requestPermission: () => Promise<NotificationPermission | "unsupported">;
}

interface UseBackgroundActivityOptions {
  /** Whether background activity features are enabled */
  enabled?: boolean;
  /** Callback when app goes to background */
  onBackground?: () => void;
  /** Callback when app returns to foreground */
  onForeground?: () => void;
  /** Whether to show notification when backgrounded (requires permission) */
  showNotification?: boolean;
  /** Custom notification options */
  notificationOptions?: {
    title?: string;
    body?: string;
    tag?: string;
  };
}

// Silent audio data URI - 1 second of silence in MP3 format (smallest possible)
const SILENT_AUDIO_SRC =
  "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIwAANP8AAAQ0a21ERVpLQ1NOUFJFU0VOVEVSSQ0KTmFtZRVHb29kQmFieSBNb25pdG9yDVllYXIVMjAyNQ1SZW1hcmtzFVNpbGVudCBhdWRpbyBmb3IgYmFja2dyb3VuZCBhY3Rpdml0eQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/7UMQFg8AAAaQAAAAgAAA0gAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==";

export function useBackgroundActivity(
  options: UseBackgroundActivityOptions = {},
): BackgroundActivityState {
  const {
    enabled = true,
    onBackground,
    onForeground,
    showNotification = true,
    notificationOptions = {},
  } = options;

  const [state, setState] = useState<
    Omit<BackgroundActivityState, "requestPermission">
  >({
    isBackgrounded: false,
    notificationPermission:
      typeof Notification !== "undefined"
        ? Notification.permission
        : "unsupported",
    webLockHeld: false,
    silentAudioPlaying: false,
  });

  // Refs for cleanup
  const silentAudioRef = useRef<HTMLAudioElement | null>(null);
  const webLockRef = useRef<any>(null);
  const notificationRef = useRef<Notification | null>(null);
  const backgroundTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // ============================================================
  // Silent Audio Playback (Android hack)
  // ============================================================

  const startSilentAudio = useCallback(() => {
    if (silentAudioRef.current) return;

    try {
      const audio = new Audio();
      audio.src = SILENT_AUDIO_SRC;
      audio.loop = true;
      audio.volume = 0.01; // Nearly silent
      audio.setAttribute("playsinline", "");

      // Play the audio
      audio
        .play()
        .then(() => {
          console.log("[BackgroundActivity] Silent audio started");
          setState((prev) => ({ ...prev, silentAudioPlaying: true }));
        })
        .catch((err) => {
          console.warn("[BackgroundActivity] Silent audio failed:", err);
        });

      silentAudioRef.current = audio;
    } catch (err) {
      console.warn("[BackgroundActivity] Failed to create silent audio:", err);
    }
  }, []);

  const stopSilentAudio = useCallback(() => {
    if (silentAudioRef.current) {
      silentAudioRef.current.pause();
      silentAudioRef.current.src = "";
      silentAudioRef.current = null;
      setState((prev) => ({ ...prev, silentAudioPlaying: false }));
      console.log("[BackgroundActivity] Silent audio stopped");
    }
  }, []);

  // ============================================================
  // Web Locks API (Prevent page discard)
  // ============================================================

  const acquireWebLock = useCallback(async () => {
    if (!("locks" in navigator)) {
      console.log("[BackgroundActivity] Web Locks API not supported");
      return;
    }

    try {
      // Request a lock that we'll hold for the duration
      await navigator.locks.request(
        "goodbaby-monitoring-lock",
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          if (lock) {
            webLockRef.current = lock;
            setState((prev) => ({ ...prev, webLockHeld: true }));
            console.log("[BackgroundActivity] Web Lock acquired");

            // Hold the lock indefinitely by returning a promise that never resolves
            return new Promise(() => {});
          } else {
            console.log(
              "[BackgroundActivity] Web Lock already held by another tab",
            );
          }
        },
      );
    } catch (err) {
      console.warn("[BackgroundActivity] Failed to acquire Web Lock:", err);
    }
  }, []);

  // ============================================================
  // Local Notifications
  // ============================================================

  /**
   * Request notification permission - should be called on user interaction
   * (like when user clicks "Start Monitoring")
   */
  const requestNotificationPermission = useCallback(async (): Promise<
    NotificationPermission | "unsupported"
  > => {
    if (typeof Notification === "undefined") {
      setState((prev) => ({ ...prev, notificationPermission: "unsupported" }));
      return "unsupported";
    }

    if (Notification.permission === "granted") {
      setState((prev) => ({ ...prev, notificationPermission: "granted" }));
      return "granted";
    }

    if (Notification.permission === "denied") {
      setState((prev) => ({ ...prev, notificationPermission: "denied" }));
      return "denied";
    }

    try {
      const permission = await Notification.requestPermission();
      setState((prev) => ({ ...prev, notificationPermission: permission }));
      console.log("[BackgroundActivity] Notification permission:", permission);
      return permission;
    } catch (err) {
      console.warn(
        "[BackgroundActivity] Failed to request notification permission:",
        err,
      );
      return "denied";
    }
  }, []);

  const showBackgroundNotification = useCallback(() => {
    if (!showNotification) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;

    // Close any existing notification
    if (notificationRef.current) {
      notificationRef.current.close();
    }

    try {
      const {
        title = "👶 GoodBaby is in background",
        body = "Tap to return and continue monitoring your baby",
        tag = "goodbaby-background",
      } = notificationOptions;

      const notification = new Notification(title, {
        body,
        tag,
        icon: "/icons/icon-192.png",
        badge: "/icons/icon-192.png",
        requireInteraction: true, // Stay visible until user interacts
        silent: true, // Don't make a sound
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      notificationRef.current = notification;
      console.log("[BackgroundActivity] Background notification shown");
    } catch (err) {
      console.warn("[BackgroundActivity] Failed to show notification:", err);
    }
  }, [showNotification, notificationOptions]);

  const closeBackgroundNotification = useCallback(() => {
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
  }, []);

  // ============================================================
  // Visibility Change Handler
  // ============================================================

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      const isHidden = document.visibilityState === "hidden";

      if (isHidden) {
        // App is going to background
        console.log("[BackgroundActivity] App backgrounded");
        setState((prev) => ({ ...prev, isBackgrounded: true }));

        // Start silent audio to keep app alive (Android)
        startSilentAudio();

        // Show notification after a short delay (to avoid showing on quick tab switches)
        // Only show if permission was already granted (we don't request here!)
        backgroundTimeoutRef.current = setTimeout(() => {
          showBackgroundNotification();
          onBackground?.();
        }, 2000);
      } else {
        // App is coming to foreground
        console.log("[BackgroundActivity] App foregrounded");
        setState((prev) => ({ ...prev, isBackgrounded: false }));

        // Cancel pending notification
        if (backgroundTimeoutRef.current) {
          clearTimeout(backgroundTimeoutRef.current);
          backgroundTimeoutRef.current = null;
        }

        // Close notification
        closeBackgroundNotification();

        // Stop silent audio
        stopSilentAudio();

        onForeground?.();
      }
    };

    // Handle page focus (additional trigger for PWAs)
    const handleFocus = () => {
      if (document.visibilityState === "visible") {
        setState((prev) => ({ ...prev, isBackgrounded: false }));
        closeBackgroundNotification();
        stopSilentAudio();
        onForeground?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Acquire web lock on mount (this doesn't require permission)
    acquireWebLock();

    // NOTE: We do NOT request notification permission here!
    // The caller should request it on user interaction (like clicking "connect")

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);

      // Cleanup
      if (backgroundTimeoutRef.current) {
        clearTimeout(backgroundTimeoutRef.current);
      }
      stopSilentAudio();
      closeBackgroundNotification();
    };
  }, [
    enabled,
    onBackground,
    onForeground,
    startSilentAudio,
    stopSilentAudio,
    showBackgroundNotification,
    closeBackgroundNotification,
    acquireWebLock,
  ]);

  return {
    ...state,
    requestPermission: requestNotificationPermission,
  };
}

/**
 * Helper hook to recover video playback after background
 * Call this with your video ref and stream to auto-recover frozen frames
 */
export function useVideoRecovery(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  stream: MediaStream | null,
) {
  const lastRecoveryRef = useRef<number>(0);
  const recoveryAttemptsRef = useRef<number>(0);

  useEffect(() => {
    if (!stream) return;

    const attemptRecovery = () => {
      const video = videoRef.current;
      if (!video) {
        console.log("[VideoRecovery] No video element found");
        return;
      }

      console.log("[VideoRecovery] Attempting recovery...", {
        hasSrcObject: !!video.srcObject,
        paused: video.paused,
        ended: video.ended,
        readyState: video.readyState,
      });

      // If video doesn't have srcObject, re-attach the stream
      if (!video.srcObject && stream) {
        console.log("[VideoRecovery] Re-attaching stream to video");
        video.srcObject = stream;
      }

      // Force play
      video.muted = true; // Ensure muted for autoplay
      video
        .play()
        .then(() => {
          console.log("[VideoRecovery] Video playback recovered");
          recoveryAttemptsRef.current = 0;
        })
        .catch((err) => {
          console.warn("[VideoRecovery] Play failed:", err);

          // Retry with re-attach after a delay
          if (recoveryAttemptsRef.current < 3) {
            recoveryAttemptsRef.current++;
            setTimeout(() => {
              if (video && stream) {
                console.log(
                  `[VideoRecovery] Retry ${recoveryAttemptsRef.current}/3`,
                );
                video.srcObject = null;
                requestAnimationFrame(() => {
                  video.srcObject = stream;
                  video.muted = true;
                  video.play().catch((e) => {
                    console.error("[VideoRecovery] Retry failed:", e);
                  });
                });
              }
            }, 200 * recoveryAttemptsRef.current);
          }
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Debounce recovery attempts
        const now = Date.now();
        if (now - lastRecoveryRef.current < 300) return;
        lastRecoveryRef.current = now;
        recoveryAttemptsRef.current = 0;

        console.log("[VideoRecovery] Page visible, initiating recovery");

        // Small delay to let browser settle
        setTimeout(attemptRecovery, 100);
      }
    };

    // Also handle focus event as backup
    const handleFocus = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        if (now - lastRecoveryRef.current < 500) return;
        lastRecoveryRef.current = now;

        console.log("[VideoRecovery] Window focused, checking video");
        setTimeout(attemptRecovery, 150);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [stream, videoRef]);
}
